import React from 'react';
import dayjs from 'utils/dayjs-timezone'; // Figure this problem out later
import { Box, Popover, PopoverTrigger, PopoverContent, Button, Icon, } from '@spothero/ui';
import IconFilledInfoCircle from '@spothero/icons/info-circle-filled';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
export const useEventOffsetNote = ({ showActions = false, }) => {
    const { event: { data: event }, city: { data: { timezone }, }, searchRequest: { starts, ends }, } = useSelector((s) => s);
    /*
        `event.starts` and `event.ends` is in UTC timezone.
        Also conversion needs to take into consideration when users timezone is different from event.
        Hence we need to consider timezone in when calculating the difference.

        Set default timezone as per the docs https://day.js.org/docs/en/timezone/set-default-timezone.
    */
    dayjs.tz.setDefault(timezone);
    const eventParkingWindowStarts = event?.parking_window?.starts;
    const eventParkingWindowEnds = event?.parking_window?.ends;
    const parkingStartOffset = dayjs
        .tz(event?.starts, 'UTC')
        .diff(dayjs.tz(eventParkingWindowStarts), 'minute');
    const parkingEndOffset = dayjs
        .tz(eventParkingWindowEnds)
        .diff(dayjs.tz(event?.ends, 'UTC'), 'minute');
    /* Offset minutes can be set to 0 or 30 in admin. Hence converting the difference to get minutes offset */
    const parkingStartOffsetHours = Math.floor(parkingStartOffset / 60);
    const parkingEndOffsetHours = Math.floor(parkingEndOffset / 60);
    const parkingStartOffsetMinutes = parkingStartOffset % 60;
    const parkingEndOffsetMinutes = parkingEndOffset % 60;
    /* isParkingStartSameAsSearchReq and isParkingEndSameAsSearchReq ensures that we do not show offset text when user updates data and time */
    const isParkingStartSameAsSearchReq = dayjs(eventParkingWindowStarts).isSame(dayjs(starts));
    const isParkingEndSameAsSearchReq = dayjs(eventParkingWindowEnds).isSame(dayjs(ends));
    const isNotAllZeroOffset = Boolean(parkingStartOffsetHours ||
        parkingStartOffsetMinutes ||
        parkingEndOffsetHours ||
        parkingEndOffsetMinutes);
    const isValidOffset = parkingStartOffsetHours >= 0 &&
        parkingStartOffsetMinutes >= 0 &&
        parkingEndOffsetHours >= 0 &&
        parkingEndOffsetMinutes >= 0;
    const showOffsetText = !showActions &&
        isParkingStartSameAsSearchReq &&
        isParkingEndSameAsSearchReq &&
        isNotAllZeroOffset &&
        isValidOffset;
    const parkingStartOffsetMinutesText = parkingStartOffsetMinutes > 0 ? '30 minutes' : '';
    const parkingEndOffsetMinutesText = parkingEndOffsetMinutes > 0 ? '30 minutes' : '';
    const parkingStartOffsetHoursText = parkingStartOffsetHours === 1
        ? `${parkingStartOffsetHours} hour`
        : parkingStartOffsetHours === 0 && parkingStartOffsetMinutes > 0
            ? ''
            : `${parkingStartOffsetHours} hours`;
    const parkingEndOffsetHoursText = parkingEndOffsetHours === 1
        ? `${parkingEndOffsetHours} hour`
        : parkingEndOffsetHours === 0 && parkingEndOffsetMinutes > 0
            ? ''
            : `${parkingEndOffsetHours} hours`;
    // reset default timezone
    dayjs.tz.setDefault();
    return {
        text: `We added ${parkingStartOffsetHoursText} 
                ${parkingStartOffsetMinutesText} before the event and 
                ${parkingEndOffsetHoursText} ${parkingEndOffsetMinutesText} after. `,
        showOffsetText,
        hasOffsets: parkingStartOffsetHours > 0 ||
            parkingStartOffsetMinutes > 0 ||
            parkingEndOffsetHours > 0 ||
            parkingEndOffsetMinutes > 0,
    };
};
export const EventOffsetNote = ({ showActions = false, }) => {
    const { text, showOffsetText } = useEventOffsetNote({ showActions });
    return (showOffsetText && (<Box fontWeight="semibold" fontSize="xs" data-testid="SpotListSearchControlsEvent-offset-note">
                {text}
                {/* Popover for additional information */}
                <Popover variant="dark" placement="top" flip={false}>
                    <PopoverTrigger>
                        <Button variant="tertiary" data-testid="SpotListSearchControlsEvent-offset-button">
                            <Icon alt="Info Circle Icon" as={IconFilledInfoCircle} fontSize="s" fill="black"/>
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent outline="0" fontWeight="normal" data-testid="SpotListSearchControlsEvent-offset-content">
                        Take some extra time and make space for life. Many of
                        our partner operators have special event rates, and give
                        you the added time at no extra cost.
                    </PopoverContent>
                </Popover>
            </Box>));
};
EventOffsetNote.propTypes = {
    showActions: PropTypes.bool,
};
